import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import * as Icons from "lucide-react";
import { HERO_FACETS, CALLIGRAPHY_IMG, TRUST, HIGHLIGHTS, HOME_TESTIMONIALS } from "../lib/content";
import { useCounter } from "../lib/useCounter";
import AnimatedCount from "../components/site/AnimatedCount";
import GeometricDivider from "../components/site/GeometricDivider";
import SectionTitle from "../components/site/SectionTitle";

const Hero = () => {
  const { count } = useCounter();
  // Static hero: lock to the "Devout" facet (clearest face, no top cut).
  const hero = HERO_FACETS[0];

  return (
    <section data-testid="hero-section" className="relative mt-0 md:-mt-20 min-h-[100vh] md:min-h-[640px] md:h-[100vh] w-full overflow-hidden pattern-emerald">
      <div className="absolute inset-0">
        {/* Mobile: top 55% image, with safe padding so head sits below the header.
            Desktop: image on right ~58%. */}
        <div className="absolute inset-x-0 top-0 h-[55vh] pt-20 md:pt-0 md:inset-y-0 md:top-0 md:h-full md:left-auto md:right-0 md:w-[58%] lg:w-[55%] bg-emerald-deep">
          <img
            src={hero.image}
            alt={hero.label}
            className="relative h-full w-full object-cover"
            style={{ objectPosition: "50% 25%" }}
          />
          {/* Soft fade from the header band into the image so any overlap is graceful. */}
          <div className="md:hidden pointer-events-none absolute inset-x-0 top-0 h-20 bg-gradient-to-b from-ivory/80 to-transparent" />
          <div className="md:hidden pointer-events-none absolute inset-x-0 bottom-0 h-24 bg-gradient-to-b from-transparent to-emerald-deep" />
        </div>
        <div className="hidden md:block absolute inset-y-0 right-0 w-[58%] lg:w-[55%] bg-gradient-to-r from-emerald-deep/95 via-emerald-deep/25 to-transparent" />
        <div className="hidden md:block absolute inset-y-0 left-0 w-[55%] bg-gradient-to-r from-emerald-deep via-emerald-deep/80 to-transparent" />
      </div>

      {/* Floating glass counter card (top-right on desktop, top-left on mobile after image) */}
      <Link
        to="/mecca-majlis"
        data-testid="hero-counter-card"
        className="hidden md:flex absolute right-8 lg:right-16 top-28 z-20 items-center gap-3 px-5 py-3 rounded-2xl bg-emerald-deep/45 backdrop-blur-md border border-gold/40 shadow-2xl hover:bg-emerald-deep/60 transition-all"
      >
        <Icons.Sparkles size={18} className="text-gold" />
        <div className="text-left">
          <p className="text-[9px] uppercase tracking-[0.3em] text-gold leading-none">Devotees in the Majlis</p>
          <p className="font-serif text-2xl text-ivory mt-1 leading-none"><AnimatedCount value={count} testId="hero-counter-glass" /></p>
        </div>
      </Link>

      <div className="relative z-10 min-h-[100vh] md:min-h-0 md:h-full container-page flex flex-col justify-end pt-[60vh] md:pt-0 pb-16 md:pb-28">
        <div className="max-w-xl lg:max-w-2xl">
          <img src={CALLIGRAPHY_IMG} alt="Calligraphic honorific" className="h-12 md:h-20 object-contain object-left opacity-95 mb-4 animate-glow" />
          <div className="mb-6" data-testid="hero-honorific">
            <p className="text-[10px] md:text-xs uppercase tracking-[0.35em] text-gold/90 mb-2">Ya Sarkar Nawaz Allah</p>
            <p className="calligraphy text-gold text-base md:text-2xl leading-snug" style={{ letterSpacing: "0.01em" }}>
              HAZRAT ABU MUHAMMAD BADE PEER<br />
              MUHIYYUL SHAH NAWAZ ALLAH<br />
              QADIR GHOUS-E-RABBANI
            </p>
          </div>
          <h1 className="font-serif text-3xl sm:text-4xl lg:text-5xl text-ivory leading-tight">
            Welcome to the Blessed World of <span className="text-gold">Hazrat Shahanshah Sufi Sarkar</span>
          </h1>
          <p className="mt-5 text-ivory/80 text-base md:text-lg max-w-2xl leading-relaxed">
            A charitable trust devoted to solving life's problems through divine blessings, healing, and compassionate service.
          </p>
          <div className="mt-8 flex flex-wrap items-center gap-4">
            <Link data-testid="hero-book-btn" to="/book" className="btn-gold">Book a Session</Link>
            <Link data-testid="hero-majlis-btn" to="/mecca-majlis" className="btn-outline-gold">Join Mecca Majlis</Link>
          </div>
        </div>

        <div className="mt-10 md:mt-14 flex flex-col md:flex-row md:flex-wrap items-start md:items-center gap-y-3 md:gap-y-4 gap-x-10">
          {HERO_FACETS.map((f, i) => (
            <Link
              key={f.key}
              to={f.href}
              data-testid={`facet-${f.key}`}
              className="group flex items-center gap-3 transition-all opacity-70 hover:opacity-100"
            >
              <span className="block h-px w-6 bg-ivory/50 transition-all duration-500 group-hover:w-10 group-hover:bg-gold" />
              <span className="flex flex-col leading-tight">
                <span className="text-[10px] uppercase tracking-[0.32em] text-gold">{`0${i + 1}`}</span>
                <span className="font-serif text-base md:text-lg text-ivory">{f.label}</span>
              </span>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
};

const MajlisBand = () => {
  const { count } = useCounter();
  return (
    <section data-testid="majlis-band" className="bg-ivory-alt/60 py-12 md:py-16">
      <div className="container-page flex flex-col md:flex-row items-center justify-between gap-6 text-center md:text-left">
        <div>
          <span className="text-[11px] uppercase tracking-[0.32em] text-gold">Mecca Majlis</span>
          <h3 className="font-serif text-2xl md:text-3xl text-emerald mt-2">
            Over <span className="text-gold"><AnimatedCount value={count} testId="band-counter" /></span> devotees have joined the Majlis
          </h3>
          <p className="text-charcoal-muted mt-2 text-sm">Place your name, receive a sacred certificate — free, private, and immediate.</p>
        </div>
        <Link to="/mecca-majlis" data-testid="band-join-btn" className="btn-gold whitespace-nowrap">Join the Majlis</Link>
      </div>
    </section>
  );
};

const Intro = () => (
  <section data-testid="intro-section" className="section-pad">
    <div className="container-page grid md:grid-cols-12 gap-12 items-start">
      <div className="md:col-span-5 reveal">
        <span className="text-[11px] uppercase tracking-[0.32em] text-gold">Who is Hazrat Sufi Sarkar</span>
        <h2 className="font-serif text-3xl md:text-5xl mt-4 text-emerald leading-tight">A Shahanshah-e-Qutub, A Revealer of Divine Truths</h2>
      </div>
      <div className="md:col-span-7 reveal">
        <p className="text-charcoal-muted text-lg leading-relaxed">
          Hazrat Shahanshah Sufi Sarkar, known as <em>Habibul Mahboob</em>, is a revered Indian Sufi saint and Qutub
          (spiritual leader) of Kerala. Honored by titles such as <span className="text-emerald font-semibold">HAZRAT ABU MUHAMMAD BADE PEER MUHIYYUL SHAH NAWAZ ALLAH QADIR GHOUS-E-RABBANI</span>, he is venerated as a divine revealer of truths and a bestower of spiritual and worldly blessings through the grace of Allah.
        </p>
        <Link to="/about" data-testid="intro-learn-more" className="inline-flex items-center mt-8 text-gold hover:text-emerald transition-colors">
          Read his journey
          <Icons.ArrowRight className="ml-2" size={16} />
        </Link>
      </div>
    </div>
  </section>
);

const Highlights = () => (
  <section data-testid="highlights-section" className="pattern-bg section-pad">
    <div className="container-page">
      <SectionTitle eyebrow="Pillars of the Path" title="Where the Sacred Meets the Everyday" subtitle="Four pillars guide the trust's work — wisdom, healing, service, and holistic care." />
      <GeometricDivider />
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
        {HIGHLIGHTS.map((h, i) => {
          const Icon = Icons[h.icon] || Icons.Sparkles;
          return (
            <div key={h.title} data-testid={`highlight-${i}`} className="card-soft p-8 reveal" style={{ transitionDelay: `${i * 80}ms` }}>
              <span className="grid place-items-center h-12 w-12 rounded-full border border-gold/50 text-gold mb-5">
                <Icon size={20} />
              </span>
              <h3 className="font-serif text-xl text-emerald">{h.title}</h3>
              <p className="text-charcoal-muted mt-3 text-sm leading-relaxed">{h.desc}</p>
            </div>
          );
        })}
      </div>
    </div>
  </section>
);

const SolveBand = () => (
  <section data-testid="solve-band" className="pattern-emerald text-ivory section-pad">
    <div className="container-page grid md:grid-cols-12 gap-10 items-center">
      <div className="md:col-span-8">
        <span className="text-[11px] uppercase tracking-[0.32em] text-gold">Solve Life Problems</span>
        <h2 className="font-serif text-3xl md:text-5xl mt-4 leading-tight">
          Through his command, blessings can flow.
        </h2>
        <p className="mt-5 text-ivory/80 text-lg max-w-2xl">
          Bring your worries — health, family, livelihood, peace of mind — and seek guidance and solutions rooted in faith.
        </p>
      </div>
      <div className="md:col-span-4 md:justify-self-end">
        <Link to="/book" data-testid="solve-book-btn" className="btn-gold">Book Online</Link>
      </div>
    </div>
  </section>
);

const Book = () => (
  <section data-testid="book-section" className="section-pad">
    <div className="container-page grid md:grid-cols-2 gap-12 items-center">
      <div className="reveal">
        <div className="mihrab-frame aspect-[4/5] overflow-hidden">
          <img
            src="https://customer-assets.emergentagent.com/job_divine-solace/artifacts/zrrdn3hx_WhatsApp%20Image%202026-05-28%20at%2021.42.25.jpeg"
            alt="Hazrat Shahanshah Sufi Sarkar with the Holy Quran — featured book"
            className="h-full w-full object-cover"
            style={{ objectPosition: "center 25%" }}
          />
        </div>
      </div>
      <div className="reveal">
        <span className="text-[11px] uppercase tracking-[0.32em] text-gold">Featured</span>
        <h2 className="font-serif text-3xl md:text-5xl mt-4 text-emerald leading-tight">The written light of Sultan-Ul-Faqr</h2>
        <p className="mt-6 text-charcoal-muted leading-relaxed">
          Discover the written teachings of Hazrat Sufi Sarkar — wisdom on love, faith, and the path to the Divine.
        </p>
        <div className="flex flex-wrap items-center gap-3 mt-8">
          <Link to="/the-book" data-testid="home-read-book" className="btn-gold">Read the Book</Link>
          <a href="/book/hazrat-book.pdf" download data-testid="home-download-pdf" className="btn-outline-gold">Download PDF</a>
        </div>
      </div>
    </div>
  </section>
);

const TestimonialsStrip = () => (
  <section data-testid="testimonials-strip" className="section-pad bg-ivory-alt/60">
    <div className="container-page">
      <SectionTitle eyebrow="Voices of the Murids" title="Stories of Solace & Blessings" />
      <div className="grid md:grid-cols-3 gap-6">
        {HOME_TESTIMONIALS.map((t, i) => (
          <figure key={t.attribution} data-testid={`testimonial-${i}`} className="card-soft p-8 reveal" style={{ transitionDelay: `${i * 80}ms` }}>
            <Icons.Quote className="text-gold mb-4" size={24} />
            <blockquote className="font-serif text-lg text-emerald leading-snug">"{t.text}"</blockquote>
            <figcaption className="mt-5 text-sm text-charcoal-muted">— {t.attribution}</figcaption>
          </figure>
        ))}
      </div>
      <div className="text-center mt-10">
        <Link to="/followers" data-testid="more-testimonials" className="btn-outline-gold">Meet the Followers</Link>
      </div>
    </div>
  </section>
);

const Closing = () => (
  <section data-testid="closing-section" className="section-pad pattern-bg">
    <div className="container-page max-w-4xl text-center reveal">
      <Icons.Sparkles className="text-gold mx-auto mb-6" size={28} />
      <p className="font-serif text-2xl md:text-4xl text-emerald leading-relaxed">
        Delve into the sacred teachings and wisdom of Hazrat Shahanshah Sufi Sarkar. Experience the transformative power of spirituality, the beauty of Sufism, and the healing traditions of Unani and Ayurvedic medicine.
      </p>
      <p className="mt-6 text-charcoal-muted text-lg">Join us on this journey toward enlightenment, love, and inner peace.</p>
      <div className="flex flex-wrap items-center justify-center gap-4 mt-10">
        <Link to="/services" data-testid="closing-services" className="btn-gold">View Services</Link>
        <Link to="/donate" data-testid="closing-donate" className="btn-outline-gold">Support the Mission</Link>
      </div>
    </div>
  </section>
);

export default function Home() {
  useEffect(() => {
    document.title = "Hazrat Sufi Sarkar — Spiritual Guidance, Healing & Charitable Service";
  }, []);
  return (
    <div data-testid="home-page">
      <Hero />
      <MajlisBand />
      <Intro />
      <Highlights />
      <SolveBand />
      <Book />
      <TestimonialsStrip />
      <Closing />
    </div>
  );
}
